import React from "react";
import { Billboard } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import { observer } from "mobx-react-lite";
import * as THREE from "three";
import { FloorplannerStoreContext } from "../../../store/floorplannerStore";
import { editorStoreContext } from "../../../store/editorStore";
import { Ruler3DType } from "../../../types/floorplannerTypes";
import {
  createRuler3DMeasurement3D,
  getRuler3DActiveColor,
  getRuler3DOrientationLabel,
  lockRuler3DEndpointToWorldAxis,
  snapRuler3DEndpointToAxisDirections,
} from "./measurement3DGeometry";
import { getWorldUnitsPerPixelAtPoint } from "./measurement3DLabelScale";
import {
  Ruler3DSnapIntersection,
  Ruler3DSnapResult,
  getRuler3DPickableIntersection,
  resolveRuler3DPlacement,
} from "./measurement3DSnapping";
import { Measurement3DObject } from "./Measurement3DLayer";
import { CANVAS_ACCENT_COLOR_HEX } from "../../../constants/brandColors";

const PREVIEW_RULER_ID = "__ruler-3d-construction-preview__";
const MIN_RULER_3D_LENGTH = 0.01;
const SNAP_INDICATOR_INNER_RADIUS_PX = 6;
const SNAP_INDICATOR_OUTER_RADIUS_PX = 10;
const SNAP_INDICATOR_FALLBACK_WORLD_UNITS_PER_PIXEL = 0.011;

type Ruler3DIntersection = Ruler3DSnapIntersection;

type Ruler3DConstructionLayerProps = {
  active: boolean;
};

const Ruler3DConstructionLayer: React.FC<Ruler3DConstructionLayerProps> =
  observer(({ active }) => {
    const editorStore = React.useContext(editorStoreContext);
    const floorplannerStore = React.useContext(FloorplannerStoreContext);
    const { camera, gl, invalidate, scene } = useThree();
    const [startPoint, setStartPoint] = React.useState<THREE.Vector3 | null>(
      null,
    );
    const [previewPoint, setPreviewPoint] =
      React.useState<THREE.Vector3 | null>(null);
    const [snapPoint, setSnapPoint] = React.useState<THREE.Vector3 | null>(
      null,
    );
    const startPointRef = React.useRef<THREE.Vector3 | null>(null);

    React.useEffect(() => {
      startPointRef.current = startPoint;
    }, [startPoint]);

    const resetConstruction = React.useCallback(() => {
      startPointRef.current = null;
      setStartPoint(null);
      setPreviewPoint(null);
      setSnapPoint(null);
      gl.domElement.style.cursor = "default";
      document.body.style.cursor = "auto";
      invalidate();
    }, [gl.domElement, invalidate]);

    React.useEffect(() => {
      if (!active) {
        resetConstruction();
        return;
      }

      gl.domElement.style.cursor = "crosshair";
      document.body.style.cursor = "crosshair";
      return () => {
        gl.domElement.style.cursor = "default";
        document.body.style.cursor = "auto";
      };
    }, [active, gl.domElement, resetConstruction]);

    const resolvePoint = React.useCallback(
      (event: PointerEvent): Ruler3DSnapResult | null =>
        getRuler3DPlacementPointFromNative(event, camera, gl.domElement, scene),
      [camera, gl.domElement, scene],
    );

    const finishRuler = React.useCallback(
      (start: THREE.Vector3, end: THREE.Vector3) => {
        if (start.distanceTo(end) <= MIN_RULER_3D_LENGTH) {
          editorStore.setRuler3DConstructionMode(false);
          resetConstruction();
          return;
        }

        const ruler: Ruler3DType = {
          id: floorplannerStore.generateId(),
          type: "ruler3D",
          measurementMode: "free",
          start: start.clone(),
          end: end.clone(),
        };

        floorplannerStore.addRuler3D(ruler);
        const addedRuler = floorplannerStore.rulers3DMap.get(ruler.id);
        if (addedRuler) {
          editorStore.clearSelections();
          floorplannerStore.selectRuler3D(addedRuler.id);
          editorStore.addSelection(addedRuler);
        }

        editorStore.setRuler3DConstructionMode(false);
        resetConstruction();
      },
      [editorStore, floorplannerStore, resetConstruction],
    );

    React.useEffect(() => {
      if (!active) return;

      const handlePointerDown = (event: PointerEvent) => {
        if (event.button !== 0) return;
        stopNativePointerEvent(event);

        const placement = resolvePoint(event);
        if (!placement) return;

        const currentStart = startPointRef.current;
        if (!currentStart) {
          setSnapPoint(placement.snapped ? placement.point.clone() : null);
          startPointRef.current = placement.point.clone();
          setStartPoint(placement.point.clone());
          setPreviewPoint(placement.point.clone());
          invalidate();
          return;
        }

        const endpoint = resolveEndpoint(placement, currentStart, event);
        setSnapPoint(
          placement.snapped || event.shiftKey ? endpoint.clone() : null,
        );
        finishRuler(currentStart, endpoint);
      };

      const handlePointerMove = (event: PointerEvent) => {
        stopNativePointerEvent(event);
        const placement = resolvePoint(event);
        if (!placement) return;

        const currentStart = startPointRef.current;
        if (!currentStart) {
          setSnapPoint(placement.snapped ? placement.point.clone() : null);
          invalidate();
          return;
        }

        const endpoint = resolveEndpoint(placement, currentStart, event);
        setPreviewPoint(endpoint);
        setSnapPoint(
          placement.snapped || event.shiftKey ? endpoint.clone() : null,
        );
        invalidate();
      };

      gl.domElement.addEventListener("pointerdown", handlePointerDown, true);
      window.addEventListener("pointermove", handlePointerMove, true);

      return () => {
        gl.domElement.removeEventListener(
          "pointerdown",
          handlePointerDown,
          true,
        );
        window.removeEventListener("pointermove", handlePointerMove, true);
      };
    }, [active, finishRuler, gl.domElement, invalidate, resolvePoint]);

    const previewMeasurement = React.useMemo(() => {
      if (!startPoint || !previewPoint) return null;
      const previewRuler: Ruler3DType = {
        id: PREVIEW_RULER_ID,
        type: "ruler3D",
        measurementMode: "free",
        start: startPoint,
        end: previewPoint,
      };
      const measurement = createRuler3DMeasurement3D(previewRuler, {
        color: getRuler3DActiveColor(previewRuler),
        lengthUnit: editorStore.displayLengthUnit,
      });
      if (measurement) {
        measurement.label = `${measurement.label} · ${getRuler3DOrientationLabel(
          previewRuler,
        )}`;
      }
      return measurement;
    }, [previewPoint, startPoint, editorStore.displayLengthUnit]);

    if (!active) return null;

    return (
      <group
        userData={{
          floorplannerType: "ruler-3d-construction-preview",
          floorplannerId: PREVIEW_RULER_ID,
        }}
      >
        {previewMeasurement && (
          <Measurement3DObject measurement={previewMeasurement} selected />
        )}
        {snapPoint && <Ruler3DSnapIndicator point={snapPoint} />}
      </group>
    );
  });

/**
 * Keeps a feature or surface snap when one was found; otherwise falls back to
 * axis snapping so free-hand measurements still line up with the world axes.
 */
function resolveEndpoint(
  placement: Ruler3DSnapResult,
  anchor: THREE.Vector3,
  event: PointerEvent,
): THREE.Vector3 {
  if (event.shiftKey) {
    return lockRuler3DEndpointToWorldAxis(placement.point, anchor);
  }
  if (placement.snapped) return placement.point.clone();
  return snapRuler3DEndpointToAxisDirections(placement.point, anchor);
}

const Ruler3DSnapIndicator: React.FC<{ point: THREE.Vector3 }> = ({
  point,
}) => {
  const { camera, size } = useThree();
  const indicatorRef = React.useRef<THREE.Group>(null);
  const initialScale =
    getWorldUnitsPerPixelAtPoint(camera, point, size.height) ??
    SNAP_INDICATOR_FALLBACK_WORLD_UNITS_PER_PIXEL;

  useFrame(({ camera: frameCamera, size: frameSize }) => {
    const indicator = indicatorRef.current;
    if (!indicator) return;
    const scale = getWorldUnitsPerPixelAtPoint(
      frameCamera,
      point,
      frameSize.height,
    );
    if (scale && Math.abs(indicator.scale.x - scale) > 1e-5) {
      indicator.scale.setScalar(scale);
    }
  });

  return (
    <Billboard
      ref={indicatorRef}
      position={point}
      scale={initialScale}
      follow
      lockX={false}
      lockY={false}
      lockZ={false}
    >
      <mesh renderOrder={1004}>
        <ringGeometry
          args={[
            SNAP_INDICATOR_INNER_RADIUS_PX,
            SNAP_INDICATOR_OUTER_RADIUS_PX,
            32,
          ]}
        />
        <meshBasicMaterial
          color={CANVAS_ACCENT_COLOR_HEX}
          depthTest={false}
          depthWrite={false}
          side={THREE.DoubleSide}
        />
      </mesh>
      <mesh position={[0, 0, 0.01]} renderOrder={1005}>
        <circleGeometry args={[2, 24]} />
        <meshBasicMaterial
          color={0xffffff}
          depthTest={false}
          depthWrite={false}
          side={THREE.DoubleSide}
        />
      </mesh>
    </Billboard>
  );
};

export function getRuler3DPlacementPointFromIntersections(
  intersections: Ruler3DIntersection[],
  fallbackPoint: THREE.Vector3 | null,
): THREE.Vector3 | null {
  const hit = getRuler3DPickableIntersection(intersections);
  return hit?.point.clone() ?? fallbackPoint?.clone() ?? null;
}

function getRuler3DPlacementPointFromNative(
  event: PointerEvent,
  camera: THREE.Camera,
  element: HTMLElement,
  scene: THREE.Scene,
): Ruler3DSnapResult | null {
  return resolveRuler3DPlacement(event, camera, element, scene);
}

function stopNativePointerEvent(event: PointerEvent) {
  if (event.cancelable) {
    event.preventDefault();
  }
  event.stopPropagation();
  event.stopImmediatePropagation?.();
}

export default Ruler3DConstructionLayer;
